import React from 'react';
import {
  Bar, BarChart, CartesianGrid, Label, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { formatNumberFR } from '../../utils/formatFR';
import useTelephone from '../../hooks/useTelephone';
import Bloc from './Bloc';
import { AXE, COULEUR_MARCHE, COULEUR_PORTEFEUILLE, GRILLE } from './couleurs';

/** Libellé complet de la tranche, coupé sur deux lignes au besoin (lot DI2 § 2). */
const TickTranche = ({ x, y, payload }) => {
  const lignes = String(payload?.value || '').split(' – ');
  return (
    <text x={x} y={y + 12} textAnchor="middle" fill={AXE} fontSize={10}>
      {lignes.length > 1
        ? lignes.map((ligne, i) => (
            <tspan key={ligne} x={x} dy={i === 0 ? 0 : 12}>{i === 0 ? `${ligne} –` : ligne}</tspan>
          ))
        : <tspan x={x}>{lignes[0]}</tspan>}
    </text>
  );
};

// lot DK4 — telephone : libelle entier sur une ligne, aligne a droite (pas de cesure Recharts)
const TickTrancheMobile = ({ x, y, payload }) => (
  <text x={x} y={y} dx={-6} dy={4} textAnchor="end" fill={AXE} fontSize={10}>
    {String(payload?.value || '')}
  </text>
);

const part = (lignes) => {
  const total = lignes.reduce((somme, l) => somme + (l.nombre || 0), 0) || 1;
  return (nombre) => Math.round((100 * (nombre || 0)) / total);
};

/** Tranches de prix — en nombre, ou en part (%) côte à côte avec le portefeuille (§ 5). */
export const GraphiqueTranches = ({ donnees, portefeuille, activiteChoisie }) => {
  const telephone = useTelephone(); // lot DK3 — barres horizontales sur téléphone
  const marche = donnees.tranches || [];
  const comparaison = Boolean(portefeuille && activiteChoisie && portefeuille.tranches);
  const partMarche = part(marche);
  const partPortefeuille = comparaison ? part(portefeuille.tranches) : null;

  const points = marche.map((t, i) => ({
    libelle: t.libelle,
    marche: comparaison ? partMarche(t.nombre) : t.nombre,
    portefeuille: comparaison ? partPortefeuille(portefeuille.tranches[i]?.nombre) : 0,
  }));

  return (
    <Bloc
      titre="Tranches de prix"
      legende={comparaison
        ? 'Part des ventes et de vos biens dans chaque tranche de prix.'
        : 'Nombre de ventes publiées dans chaque tranche de prix.'}
      testid="observatoire-graph-tranches"
    >
      <div className={`${telephone ? 'h-80' : 'h-64'} -ml-2`}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={points}
            layout={telephone ? 'vertical' : 'horizontal'}
            margin={telephone
              ? { top: 8, right: 12, bottom: 28, left: 8 }
              : { top: 8, right: 8, bottom: 40, left: 16 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={GRILLE}
                           vertical={telephone} horizontal={!telephone} />
            {telephone ? (
              <>
                <YAxis type="category" dataKey="libelle" width={132} interval={0}
                       tickLine={false} tick={<TickTrancheMobile />} />
                <XAxis type="number" allowDecimals={false}
                       tick={{ fontSize: 11, fill: AXE }} tickFormatter={formatNumberFR} height={40}>
                  <Label value={comparaison ? 'Part (%)' : 'Nombre de ventes'}
                         position="insideBottom" offset={-18}
                         style={{ fontSize: 11, fill: AXE }} />
                </XAxis>
              </>
            ) : (
              <>
            <XAxis dataKey="libelle" tick={<TickTranche />} interval={0} height={44}>
              <Label value="Tranche de prix (€)" position="insideBottom" offset={-34}
                     style={{ fontSize: 11, fill: AXE }} />
            </XAxis>
            <YAxis tick={{ fontSize: 11, fill: AXE }} allowDecimals={false} tickFormatter={formatNumberFR}>
              <Label value={comparaison ? 'Part (%)' : 'Nombre de ventes'} angle={-90}
                     position="insideLeft" offset={-4}
                     style={{ fontSize: 11, fill: AXE, textAnchor: 'middle' }} />
            </YAxis>
              </>
            )}
            <Tooltip
              formatter={(valeur, nom) => [comparaison ? `${valeur} %` : `${formatNumberFR(valeur)} ventes`, nom]}
              labelFormatter={(l) => `Tranche ${l}`}
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
            />
            <Legend verticalAlign="top" height={24} wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="marche" name={comparaison ? 'Marché (part des ventes, %)' : 'Ventes publiées (marché)'}
                 fill={COULEUR_MARCHE} radius={telephone ? [0, 3, 3, 0] : [3, 3, 0, 0]} />
            {comparaison && (
              <Bar dataKey="portefeuille" name="Votre portefeuille (part, %)"
                   fill={COULEUR_PORTEFEUILLE} radius={telephone ? [0, 3, 3, 0] : [3, 3, 0, 0]} />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
      {portefeuille && !activiteChoisie && (
        <p className="text-xs text-slate-500 mt-2" data-testid="observatoire-graph-tranches-note">
          Choisissez une activité pour comparer la répartition de vos biens.
        </p>
      )}
    </Bloc>
  );
};

export default GraphiqueTranches;
